import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Checkbox,
  LinearProgress,
  Chip,
  Alert,
  IconButton,
  Divider,
  Stack,
  Avatar,
  Tooltip,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  CheckCircle,
  Refresh,
  FileDownload,
  Save,
  Warning,
  Schedule,
  LocationOn,
  Inventory,
  PointOfSale,
  Analytics,
  Business,
  Dashboard as DashboardIcon,
  CalendarMonth, ChevronLeft, ChevronRight, ExpandMore
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import GroupsIcon from '@mui/icons-material/Groups';
import {api} from '../config/API_URL';
import ComplianceCalendar from '../components/Calender';
import TeamManagement from './TeamManagement';
import AnalyticsDashboard from './AnalyticsDashboard';
import SalesDashboard from './SalesDashboard';
import InventoryDashboard from './InventoryDashboard';
export default function Dashboard() {
  console.log('Dashboard')

  const [user, setUser] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [viewMode, setViewMode] = useState('daily');
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' or 'calendar'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [expandedSections, setExpandedSections] = useState({
    'Opening': true,
    'Mid-Day': true,
    'Closing': true
  });

  const toggleSection = (timeOfDay) => {
    setExpandedSections(prev => ({
      ...prev,
      [timeOfDay]: !prev[timeOfDay]
    }));
  };

  useEffect(() => {
    loadData();
  }, [selectedDate]);

const loadData = async () => {
    try {
      const [userRes, checklistRes] = await Promise.all([
        api.get('/user'),
        api.get(`/checklist/today?date=${selectedDate}`)
      ]);

      setUser(userRes.data);
      setTasks(checklistRes.data.tasks);
      setChecklist(checklistRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Load failed:', err);
      setLoading(false);
    }
  };

  const navigateDate = (direction) => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + direction);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(date);
    selected.setHours(0, 0, 0, 0);

    if (selected.getTime() === today.getTime()) {
      return 'Today';
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (selected.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    }

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (selected.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    }

    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isFutureDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate + 'T00:00:00');
    selected.setHours(0, 0, 0, 0);
    return selected > today;
  };

  // Helper to check if selected date is in the past
  const isPastDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate + 'T00:00:00');
    selected.setHours(0, 0, 0, 0);
    return selected < today;
  };

  const toggleTask = (id) => {
    // Prevent toggling for future or past dates
    if (isFutureDate() || isPastDate()) {
      return;
    }
    setTasks(prev => prev.map(t => 
      t.id === id ? { ...t, done: !t.done } : t
    ));
  };
  const saveIndividualTask = async (taskId, isDone, event) => {
    event.stopPropagation();
    if (isFutureDate()) {
      return;
    }
    try {
      const completed = tasks.filter(t => t.id === taskId ? isDone : t.done).map(t => t.id);
      await api.post('/checklist/save', { completed_tasks: completed });
      setSaveStatus('Task saved!');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (err) {
      setSaveStatus('❌ Save failed');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  const saveProgress = async () => {
    const completed = tasks.filter(t => t.done).map(t => t.id);
    await api.post('/checklist/save', { completed_tasks: completed });
    setSaveStatus('Saved successfully!');
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const exportPDF = async () => {
    const res = await api.post('/export-pdf', { tasks });
    setSaveStatus(`PDF ready: ${res.data.filename}`);
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const triggerScrape = async () => {
    setScraping(true);
    try {
      const res = await api.post('/scrape-now', { 
        license_type: user.license_type 
      });
      setSaveStatus(`✅ Updated ${res.data.tasks_found} tasks`);
      loadData();
    } catch (err) {
      setSaveStatus('❌ Update failed');
    }
    setScraping(false);
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const saveAndExport = async (taskType = 'daily') => {
      const managerName = prompt("Enter your name (closing manager):");
      try {
        const response = await api.post('/checklist/save', {
          completed_tasks: tasks.filter(t => t.done).map(t => t.id),
          export_pdf: true,
          signed_by: managerName || "Manager",
          task_type: taskType
        }, {
          responseType: 'blob' // Important: tell axios to expect a file
        });
        
        // Create a download link and trigger it
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `compliance-${taskType}-${selectedDate}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setSaveStatus(`${taskType === 'daily' ? 'Daily' : 'As-Needed'} report downloaded!`);
      } catch (error) {
        console.error('Export failed:', error);
        setSaveStatus('❌ Export failed');
      }
      setTimeout(() => setSaveStatus(''), 3000);
  };
  const markAudited = async () => {
    await api.post('/audit-day', { date: selectedDate });
    setSaveStatus('Day marked as audited');
  };

  const handleViewChange = (event, newValue) => {
    setCurrentView(newValue);
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <Refresh sx={{ fontSize: 60, color: 'white' }} />
          </motion.div>
          <Typography variant="h6" sx={{ color: 'white', mt: 2 }}>
            Loading compliance data...
          </Typography>
        </Box>
      </Box>
    );
  }

  const done = tasks.filter(t => t.done).length;
  const total = tasks.length;
  const progress = (done / total) * 100;

  const categoryColors = {
    'Personnel': '#3b82f6',
    'Access Control': '#8b5cf6',
    'Inventory': '#10b981',
    'Security': '#ef4444',
    'Operations': '#f59e0b',
    'Compliance': '#06b6d4',
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4,
      px: 2,
    }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header Card */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card sx={{
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontSize: 32,
                    fontWeight: 700,
                  }}>
                    {user?.org?.name?.split(' ').map(w => w[0]).join('') || 'ORG'}  
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {user?.org?.name || 'Organization Name'}  
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                      <Chip
                        icon={<LocationOn />}
                        label={user?.org?.address}
                        size="small"
                        sx={{ fontWeight: 500 }} />
                      <Chip
                        icon={<Business />}
                        label={`${user?.org?.state} · ${user?.org?.license_type}`}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 500, textTransform: 'capitalize' }} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Schedule sx={{ fontSize: 14 }} />
                      Last updated: {checklist?.updated_at ? new Date(checklist.updated_at).toLocaleString() : 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  startIcon={scraping ? <Refresh className="spin" /> : <Refresh />}
                  onClick={triggerScrape}
                  disabled={scraping}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(102, 126, 234, 0.6)',
                    },
                  }}
                >
                  {scraping ? 'Updating...' : 'Update Regs'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>

        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
        >
          <Card sx={{
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}>
            <Tabs
              value={currentView}
              onChange={handleViewChange}
              sx={{
                px: 2,
                '& .MuiTabs-indicator': {
                  backgroundColor: '#667eea',
                  height: 3,
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  minHeight: 64,
                  '&.Mui-selected': {
                    color: '#667eea',
                  },
                },
              }}
            >
              <Tab
                value="dashboard"
                label="Compliance Dashboard"
                icon={<DashboardIcon />}
                iconPosition="start" />
              <Tab
                value="calendar"
                label="Calendar"
                icon={<CalendarMonth />}
                iconPosition="start" />
              <Tab
                value="team"
                label="Team Management"
                icon={<GroupsIcon />}
                iconPosition="start" />
                <Tab value="inventory" label="Inventory" 
                  icon={<Inventory />} 
                  iconPosition="start"  />
                <Tab value="sales" label="Sales Report" 
                  icon={<PointOfSale />} 
                  iconPosition="start" />
                <Tab value="analytics" label="Analytics" 
                  icon={<Analytics />} 
                  iconPosition="start" />
            </Tabs>
          </Card>
        </motion.div>

        {/* Status Alert */}
        <AnimatePresence>
          {saveStatus && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Alert
                severity={saveStatus.includes('❌') ? 'error' : 'success'}
                sx={{ mb: 3, fontWeight: 500 }}
              >
                {saveStatus}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Conditional Content Based on Current View */}
        {currentView === 'dashboard' && (
          <>
            {/* Progress Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card sx={{
                mb: 3,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Daily Compliance Progress
                    </Typography>
                    {/* <Typography variant="h4" sx={{ fontWeight: 700, color: progress === 100 ? 'success.main' : 'primary.main' }}>
                      {Math.round(progress)}%
                    </Typography> */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton 
                      onClick={() => navigateDate(-1)}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          borderColor: 'primary.main',
                        }
                      }}
                    >
                      <ChevronLeft />
                    </IconButton>
                    
                    <Chip
                      label={formatDisplayDate(selectedDate)}
                      onClick={goToToday}
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        px: 2,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                        }
                      }}
                    />
                    
                    <IconButton 
                      onClick={() => navigateDate(1)}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          borderColor: 'primary.main',
                        }
                      }}
                    >
                      <ChevronRight />
                    </IconButton>
                  </Stack>
                </Stack>

                {/* Existing progress bar code */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedDate === new Date().toISOString().split('T')[0] 
                      ? 'Today\'s Progress' 
                      : formatDisplayDate(selectedDate)}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: progress === 100 ? 'success.main' : 'primary.main' }}>
                    {Math.round(progress)}%
                  </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 6,
                        background: progress === 100
                          ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                          : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      },
                    }} />

                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {done} of {total} tasks complete
                    </Typography>
                    {progress === 100 ? (
                      <Chip
                        icon={<CheckCircle />}
                        label="All Done!"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600 }} />
                    ) : (
                      <Chip
                        icon={<Warning />}
                        label={`${total - done} remaining`}
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 600 }} />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>

            {/* Tasks Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              position: 'relative', // Important for overlay positioning
            }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 4, pb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Compliance Checklist
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isFutureDate() 
                          ? 'Future date - view only' 
                          : isPastDate()
                          ? 'Past date - view only'
                          : 'Click tasks to mark as complete'}
                      </Typography>
                    </Box>

                    {/* Show alert if viewing past/future date */}
                    {selectedDate !== new Date().toISOString().split('T')[0] && (
                      <Alert 
                        severity={isFutureDate() ? "warning" : "info"} 
                        sx={{ py: 0.5 }}
                      >
                        {isFutureDate() 
                          ? `Viewing future date: ${formatDisplayDate(selectedDate)}` 
                          : `Viewing past date: ${formatDisplayDate(selectedDate)}`}
                      </Alert>
                    )}
                  </Stack>

                  {/* Task view mode buttons */}
                  <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                    <Button
                      variant={viewMode === 'daily' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('daily')}
                      size="small"
                    >
                      Daily Tasks
                    </Button>
                    <Button
                      variant={viewMode === 'as-needed' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('as-needed')}
                      size="small"
                    >
                      As Needed
                    </Button>
                    {/* <Button
                      variant={viewMode === 'monthly' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('monthly')}
                      size="small"
                      disabled
                    >
                      Monthly (Coming Soon)
                    </Button> */}
                  </Stack>

                  <Button
                    onClick={() => {
                      const end = new Date().toISOString().split('T')[0];
                      const start = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
                      window.location.href = `/api/export-range?start=${start}&end=${end}`;
                    }}
                  >
                    Export 30 Days
                  </Button>
                </Box>

                <Divider />

                {/* Task List Container with Overlay */}
                <Box sx={{ position: 'relative' }}>
                  {/* FUTURE DATE OVERLAY */}
                  {isFutureDate() && (
                    <Box
                      sx={{
                        // position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.92)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                        borderRadius: 1,
                      }}
                    >
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Schedule 
                          sx={{ 
                            fontSize: 80, 
                            color: 'warning.main',
                            mb: 2 
                          }} 
                        />
                      </motion.div>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 700,
                          color: 'text.primary',
                          textAlign: 'center'
                        }}
                      >
                        Come Back Tomorrow!
                      </Typography>
                      <Typography 
                        variant="body1" 
                        color="text.secondary"
                        sx={{ textAlign: 'center', maxWidth: 400 }}
                      >
                        Tasks for {formatDisplayDate(selectedDate)} will be available on that date.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<CheckCircle />}
                        onClick={goToToday}
                        sx={{ mt: 2 }}
                      >
                        Go to Today
                      </Button>
                    </Box>
                  )}

                  {/* PAST DATE OVERLAY (Optional - for read-only past dates) */}
                  {isPastDate() && (
  <Box sx={{ p: 2, backgroundColor: 'info.light' }}>
    <Alert 
      severity="info"
      action={
        <Button
          size="small"
          variant="outlined"
          onClick={goToToday}
        >
          Go to Today
        </Button>
      }
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Viewing historical data for {formatDisplayDate(selectedDate)} - Read only
      </Typography>
    </Alert>
  </Box>
)}

                  {/* Actual Task List */}
                  <Box sx={{ p: 2 }}>
                    {viewMode === 'daily' && (
                      <>
                        {['Opening', 'Mid-Day', 'Closing'].map((timeOfDay) => {
                          const timeTasks = tasks.filter(t => t.time_of_day === timeOfDay);
                          if (timeTasks.length === 0) return null;

                          return (
                            <Box key={timeOfDay} sx={{ mb: 4 }}>
                              <Box
                                onClick={() => toggleSection(timeOfDay)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  cursor: 'pointer',
                                  mb: 2,
                                  p: 1,
                                  borderRadius: 1,
                                  '&:hover': {
                                    backgroundColor: 'rgba(102, 126, 234, 0.05)',
                                  }
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    color: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}
                                >
                                  {timeOfDay === 'Opening' && '🌅'}
                                  {timeOfDay === 'Mid-Day' && '☀️'}
                                  {timeOfDay === 'Closing' && '🌙'}
                                  {timeOfDay} Tasks ({timeTasks.length})
                                </Typography>
                                <ExpandMore
                                  sx={{
                                    transform: expandedSections[timeOfDay] ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s ease',
                                    color: 'primary.main'
                                  }}
                                />
                              </Box>
                              {expandedSections[timeOfDay] && (
                              <AnimatePresence>
                                {timeTasks.map((task, index) => (
                                  <motion.div
                                    key={task.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                  >
                                    <Card
                                      sx={{
                                        mb: 2,
                                        cursor: 'default',
                                        transition: 'all 0.3s ease',
                                        border: '2px solid',
                                        borderColor: task.done ? 'success.main' : 'grey.200',
                                        background: task.done
                                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)'
                                          : 'white',
                                        opacity: isFutureDate() ? 0.6 : 1,
                                        '&:hover': (isFutureDate() || isPastDate()) ? {} : {
                                          borderColor: task.done ? 'success.dark' : 'primary.main',
                                          boxShadow: 3,
                                          transform: 'translateY(-2px)',
                                        },
                                      }}
                                      onClick={() => toggleTask(task.id)}
                                    >
                                      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                                        <Stack direction="row" spacing={2} alignItems="flex-start">
                                          <Checkbox
                                            checked={task.done}
                                            disabled={isFutureDate() || isPastDate()}
                                            sx={{
                                              mt: -1,
                                              '& .MuiSvgIcon-root': { fontSize: 28 },
                                            }}
                                          />
                                          <Box sx={{ flex: 1 }}>
                                            <Typography
                                              variant="body1"
                                              sx={{
                                                fontWeight: 600,
                                                textDecoration: task.done ? 'line-through' : 'none',
                                                color: task.done ? 'text.secondary' : 'text.primary',
                                                mb: 1,
                                              }}
                                            >
                                              {task.text}
                                            </Typography>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                              <Chip
                                                label={task.ref}
                                                size="small"
                                                sx={{
                                                  fontFamily: 'monospace',
                                                  fontWeight: 600,
                                                  fontSize: 11,
                                                  height: 24,
                                                }}
                                              />
                                              <Chip
                                                label={task.category}
                                                size="small"
                                                sx={{
                                                  backgroundColor: categoryColors[task.category] || '#6b7280',
                                                  color: 'white',
                                                  fontWeight: 600,
                                                  fontSize: 11,
                                                  height: 24,
                                                }}
                                              />
                                            </Stack>
                                          </Box>
                                          {task.done && !isFutureDate() && !isPastDate() && (
                                            <>
                                              <Tooltip title="Save this task">
                                                <IconButton
                                                  size="small"
                                                  onClick={(e) => saveIndividualTask(task.id, task.done, e)}
                                                  sx={{
                                                    opacity: 0.7,
                                                    '&:hover': {
                                                      opacity: 1,
                                                      backgroundColor: 'primary.light',
                                                    },
                                                  }}
                                                >
                                                  <Save fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              <motion.div
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{ type: 'spring', stiffness: 500 }}
                                              >
                                                <CheckCircle color="success" sx={{ fontSize: 28 }} />
                                              </motion.div>
                                            </>
                                          )}
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  </motion.div>
                                ))}
                              </AnimatePresence>
                              )}
                            </Box>
                          );
                        })}
                      </>
                    )}

                    {viewMode === 'as-needed' && (
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            fontWeight: 700,
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          ⚡ As Needed Tasks
                        </Typography>

                        <AnimatePresence>
                          {tasks.filter(t => t.time_of_day === 'As Needed').map((task, index) => (
                            <motion.div
                              key={task.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                              <Card
                                sx={{
                                  mb: 2,
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  border: '2px solid',
                                  borderColor: task.done ? 'success.main' : 'grey.200',
                                  background: task.done
                                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)'
                                    : 'white',
                                  '&:hover': {
                                    borderColor: task.done ? 'success.dark' : 'primary.main',
                                    boxShadow: 3,
                                    transform: 'translateY(-2px)',
                                  },
                                }}
                                onClick={() => toggleTask(task.id)}
                              >
                                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                                  <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Checkbox
                                      checked={task.done}
                                      sx={{
                                        mt: -1,
                                        '& .MuiSvgIcon-root': { fontSize: 28 },
                                      }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 600,
                                          textDecoration: task.done ? 'line-through' : 'none',
                                          color: task.done ? 'text.secondary' : 'text.primary',
                                          mb: 1,
                                        }}
                                      >
                                        {task.text}
                                      </Typography>
                                      <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Chip
                                          label={task.ref}
                                          size="small"
                                          sx={{
                                            fontFamily: 'monospace',
                                            fontWeight: 600,
                                            fontSize: 11,
                                            height: 24,
                                          }} />
                                        <Chip
                                          label={task.category}
                                          size="small"
                                          sx={{
                                            backgroundColor: categoryColors[task.category] || '#6b7280',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: 11,
                                            height: 24,
                                          }} />
                                      </Stack>
                                    </Box>
                                    {task.done && (
                                      <>
                                        <Tooltip title="Save this task">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => saveIndividualTask(task.id, task.done, e)}
                                            sx={{
                                              opacity: 0.7,
                                              '&:hover': {
                                                opacity: 1,
                                                backgroundColor: 'primary.light',
                                              },
                                            }}
                                          >
                                            <Save fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <motion.div
                                          initial={{ scale: 0 }}
                                          animate={{ scale: 1 }}
                                          transition={{ type: 'spring', stiffness: 500 }}
                                        >
                                          <CheckCircle color="success" sx={{ fontSize: 28 }} />
                                        </motion.div>
                                      </>
                                    )}
                                  </Stack>
                                </CardContent>
                              </Card>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </Box>
                    )}

                    {viewMode === 'monthly' && (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                          Monthly tasks coming soon...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  </Box> 
                  <Divider />

                  {/* Action Buttons */}
                  <Box sx={{ p: 4, background: 'rgba(249, 250, 251, 0.5)' }}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" startIcon={<Save />} onClick={saveProgress}>
                        Save Only
                      </Button>
                      
                      {/* Separate buttons for each report type */}
                      <Button 
                        variant="contained" 
                        startIcon={<FileDownload />} 
                        onClick={() => saveAndExport('daily')}
                        disabled={viewMode !== 'daily'}
                      >
                        Save + Daily PDF
                      </Button>
                      
                      <Button 
                        variant="contained"
                        color="secondary"
                        startIcon={<FileDownload />} 
                        onClick={() => saveAndExport('as-needed')}
                        disabled={viewMode !== 'as-needed'}
                      >
                        Save + As-Needed PDF
                      </Button>
                      {/* <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<CheckCircle />}
                        onClick={markAudited}
                      >
                        Mark Audited
                      </Button> */}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </>
        )}

        {/* Calendar View */}
        
        {currentView === 'calendar' && (
          <ComplianceCalendar />
        )}
        {currentView === 'team' && (
          <TeamManagement />
        )}

        {currentView === 'inventory' && (
          <InventoryDashboard />
        )}

        {currentView === 'sales' && <SalesDashboard />}
        {currentView === 'analytics' && <AnalyticsDashboard />}

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Data sourced from:{' '}
            <a
            href={checklist?.source_url || 'https://masscannabiscontrol.com/wp-content/uploads/2024/11/241122-935-CMR-500-Adult-Use-Regulations.pdf'}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'white', fontWeight: 600, textDecoration: 'underline' }}
            >
            MA Cannabis Control Commission
          </a>
        </Typography>
      </Box>
    </Box><style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
}